import React from 'react';
import { Button, Tooltip as AntdTooltip } from 'antd';
import { DownOutlined, RightOutlined, UpOutlined } from '@ant-design/icons';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import MetricWithDelta from './MetricWithDelta';
import { VIEW_MODE } from './LifeCycleAnalyticsConstants';
import { formatDurationShort, roundToOneDecimalPlace } from './LifeCycleAnalyticsUtils';

export type LifeCyclePriorityTableColumnsArgs = {
	selectedPriorityId: number | string | null;
	viewMode: string;
	handleArrowClick: (priorityId: number | string) => void;
	renderSpend: (value: number) => React.ReactNode;
};

export function buildLifeCyclePriorityTableColumns({
	selectedPriorityId,
	viewMode,
	handleArrowClick,
	renderSpend,
}: LifeCyclePriorityTableColumnsArgs) {
	return [
		{
			title: 'Priority',
			dataIndex: 'woPriorityName',
			key: 'woPriorityName',
			width: '20%',
			render: (_, record) => (
				<div className="inline-block min-w-0 max-w-full align-middle">
					<AntdTooltip title={record.woPriorityName}>
						<span
							className="inline-block min-w-0 max-w-full truncate rounded-2xl px-3 py-1 align-middle text-sm font-medium leading-[22px]"
							style={{
								color: record.color,
								backgroundColor: record.bgColor,
							}}
						>
							{record.woPriorityName}
						</span>
					</AntdTooltip>
				</div>
			),
		},
		{
			title: 'Completed Work Orders',
			dataIndex: 'completedWoCount',
			key: 'completedWoCount',
			width: '14%',
			render: (value) => {
				const displayValue = value || 0;
				return <span className="bodyregular text-character-primary">{displayValue}</span>;
			},
		},
		{
			title: 'Spend',
			dataIndex: 'spend',
			key: 'spend',
			width: '12%',
			render: (value) => {
				const spendDisplay = value ? (
					<span className="bodyregular text-character-primary">{renderSpend(value)}</span>
				) : (
					'--'
				);
				return <MetricWithDelta value={spendDisplay} changePercent={null} />;
			},
		},
		{
			title: 'Avg Approval Time',
			dataIndex: 'avgApprovalTimeSeconds',
			key: 'avgApprovalTimeSeconds',
			width: '13%',
			render: (value, record) => {
				const changes = nullSafeGet('changes', record);
				return (
					<MetricWithDelta
						value={formatDurationShort(value || 0)}
						changePercent={nullSafeGet('avgApprovalTimeSeconds', changes)}
					/>
				);
			},
		},
		{
			title: 'Avg Response Time',
			dataIndex: 'avgResponseTimeSeconds',
			key: 'avgResponseTimeSeconds',
			width: '13%',
			render: (value, record) => {
				const changes = nullSafeGet('changes', record);
				return (
					<MetricWithDelta
						value={formatDurationShort(value || 0)}
						changePercent={nullSafeGet('avgResponseTimeSeconds', changes)}
					/>
				);
			},
		},
		{
			title: 'Avg Resolution Time',
			dataIndex: 'avgResolutionTimeSeconds',
			key: 'avgResolutionTimeSeconds',
			width: '13%',
			render: (value, record) => {
				const changes = nullSafeGet('changes', record);
				return (
					<MetricWithDelta
						value={formatDurationShort(value || 0)}
						changePercent={nullSafeGet('avgResolutionTimeSeconds', changes)}
					/>
				);
			},
		},
		{
			title: 'First Time Fix Rate',
			dataIndex: 'firstTimeFixRate',
			key: 'firstTimeFixRate',
			width: '12%',
			render: (value, record) => {
				const changes = nullSafeGet('changes', record);
				return (
					<MetricWithDelta
						value={`${roundToOneDecimalPlace(value || 0)}%`}
						changePercent={nullSafeGet('firstTimeFixRate', changes)}
						higherIsBetter={true}
					/>
				);
			},
		},
		{
			title: '',
			dataIndex: 'action',
			key: 'action',
			width: 60,
			className: 'print:!hidden',
			onHeaderCell: () => ({ className: 'print:!hidden' }),
			render: (_, record) => {
				const isSelected = selectedPriorityId === record.woPriorityId && viewMode !== VIEW_MODE.ALL;
				const icon =
					viewMode === VIEW_MODE.ALL || !isSelected ? (
						<RightOutlined />
					) : viewMode === VIEW_MODE.SINGLE ? (
						<DownOutlined />
					) : (
						<UpOutlined />
					);
				return (
					<Button
						type="link"
						className="!p-0 !text-character-secondary"
						onClick={(e) => {
							e.stopPropagation();
							handleArrowClick(record.woPriorityId);
						}}
						icon={icon}
					/>
				);
			},
		},
	];
}
